import { useEffect, useMemo, useState } from 'react';
import './FairWelcome.css';
import FairQuiz from './FairQuiz';

// Fair dates (local time): Oct 29 to Nov 7, 2026
const FAIR_START = new Date("2026-10-29T00:00:00+01:00");
const FAIR_END = new Date("2026-11-07T23:59:59+01:00");

const NUDGE_DELAY_MS = 5000;

// Edit the wording here
const FAIR_NAME = 'Sila';
const COPY = {
  eyebrow: `${FAIR_NAME} 2026`,
  italic: "I'm so glad you found your way here.",
  text: "Whether we met at the fair or you're just discovering my work, welcome. Take your time and explore the book.",
  button: 'Explore the book',
  nudgeTitle: 'Not sure this book is for you?',
  nudgeText: 'Take a quick quiz to find out.',
  nudgeButton: 'Take the quiz',
  nudgeLater: 'Maybe later',
};

const COLORS = ['#dcc6e0', '#8c6e94', '#e5d3b3', '#6e4f38', '#ebd5e2'];

function hasFairLink() {
  return new URLSearchParams(window.location.search).get('from') === 'fair';
}

function isFairVisitor() {
  const now = new Date();
  return hasFairLink() || (now >= FAIR_START && now <= FAIR_END);
}

function readFlag(key) {
  try { return sessionStorage.getItem(key) === '1'; } catch { return false; }
}

function setFlag(key) {
  try { sessionStorage.setItem(key, '1'); } catch {}
}

export default function FairWelcome() {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(false); // welcome is done, start the nudge timer
  const [nudge, setNudge] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  const confetti = useMemo(
    () =>
      Array.from({ length: 45 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 1.2,
        duration: 2.8 + Math.random() * 2,
        size: 6 + Math.random() * 8,
        drift: (Math.random() - 0.5) * 120,
        round: Math.random() > 0.5,
        color: COLORS[i % COLORS.length],
      })),
    []
  );

  useEffect(() => {
    if (!isFairVisitor()) return;

    // ?from=fair is a testing shortcut: resets everything
    if (hasFairLink()) {
      try {
        sessionStorage.removeItem('fairWelcomeSeen');
        sessionStorage.removeItem('fairNudgeDismissed');
      } catch {}
    }

    if (readFlag('fairWelcomeSeen')) {
      setArmed(true);
    } else {
      setOpen(true);
    }
  }, []);

  // Start the 5 second timer once the welcome is closed
  useEffect(() => {
    if (!armed || readFlag('fairNudgeDismissed')) return;
    const t = setTimeout(() => setNudge(true), NUDGE_DELAY_MS);
    return () => clearTimeout(t);
  }, [armed]);

  // Escape to close + lock page scroll while the welcome is open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    setFlag('fairWelcomeSeen');
    setArmed(true);
  };

  const dismissNudge = () => {
    setNudge(false);
    setFlag('fairNudgeDismissed');
  };

  const goToQuiz = () => {
    dismissNudge();
    setQuizOpen(true);
  };

  return (
    <>
      {open && (
        <div className="fair-overlay" onClick={close}>
          <div className="fair-confetti" aria-hidden="true">
            {confetti.map((c) => (
              <span
                key={c.id}
                className="fair-piece"
                style={{
                  left: `${c.left}%`,
                  width: c.size,
                  height: c.round ? c.size : c.size * 1.6,
                  borderRadius: c.round ? '50%' : '2px',
                  background: c.color,
                  animationDelay: `${c.delay}s`,
                  animationDuration: `${c.duration}s`,
                  '--drift': `${c.drift}px`,
                }}
              />
            ))}
          </div>

          <div
            className="fair-card"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="fair-eyebrow">{COPY.eyebrow}</p>
            <h2>
              Welcome from <span className="fair-highlight">{FAIR_NAME}</span>
            </h2>
            <p className="fair-italic">{COPY.italic}</p>
            <p className="fair-text">{COPY.text}</p>
            <button className="fair-btn" onClick={close}>
              {COPY.button}
            </button>
          </div>
        </div>
      )}

      {nudge && (
        <div className="fair-nudge" role="dialog" aria-label="Quiz suggestion">
          <button
            className="fair-nudge-x"
            onClick={dismissNudge}
            aria-label="Close"
          >
            ×
          </button>
          <p className="fair-nudge-title">{COPY.nudgeTitle}</p>
          <p className="fair-nudge-text">{COPY.nudgeText}</p>
          <div className="fair-nudge-actions">
            <button className="fair-btn" onClick={goToQuiz}>
              {COPY.nudgeButton}
            </button>
            <button className="fair-nudge-later" onClick={dismissNudge}>
              {COPY.nudgeLater}
            </button>
          </div>
        </div>
      )}

      <FairQuiz open={quizOpen} onClose={() => setQuizOpen(false)} />
    </>
  );
}